import React, { useState, useEffect } from "react";
import { Instagram, Facebook, Menu, X } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { BRAND } from "../lib/i18n";

const WhatsAppIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const TikTokIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.84-0Z" />
  </svg>
);

export { WhatsAppIcon, TikTokIcon };

const NAV_IDS = [
  { id: "services", key: "services" },
  { id: "gallery", key: "gallery" },
  { id: "why-us", key: "whyUs" },
  { id: "reviews", key: "reviews" },
  { id: "contact", key: "contact" },
];

const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div
        className={`container-hd flex items-center justify-between rounded-2xl transition-all duration-500 ${
          scrolled
            ? "glass px-4 md:px-6 py-3"
            : "px-0 py-0"
        }`}
        style={scrolled ? { margin: "0 1rem" } : {}}
      >

        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3"
          data-testid="logo-link"
        >
          <img
            src={BRAND.logoUrl}
            alt="Hetlav Detailing Wien – Logo für mobile Autoaufbereitung"
            width="44"
            height="44"
            className="w-11 h-11 rounded-full object-cover border border-white/10"
          />
          <div className="hidden sm:block leading-none">
            <div className="font-display text-[15px] font-bold text-white tracking-wide">
              HETLAV
            </div>
            <div className="font-body text-[10px] tracking-[0.3em] text-[color:var(--hd-gold)] uppercase">
              Detailing
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_IDS.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="font-body text-sm text-white/70 hover:text-[color:var(--hd-gold)] transition-colors uppercase tracking-wider"
              data-testid={`nav-${item.id}`}
            >
              {t.nav[item.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">

          <div className="hidden md:flex items-center gap-2 pr-2 border-r border-white/10 mr-1">
            <a
              href={BRAND.instagram}
              target="_blank"
              rel="noreferrer"
              className="text-white/60 hover:text-[color:var(--hd-gold)] transition-colors p-1"
              data-testid="header-instagram"
              aria-label="Instagram"
            >
              <Instagram size={16} />
            </a>
            <a
              href={BRAND.facebook}
              target="_blank"
              rel="noreferrer"
              className="text-white/60 hover:text-[color:var(--hd-gold)] transition-colors p-1"
              data-testid="header-facebook"
              aria-label="Facebook"
            >
              <Facebook size={16} />
            </a>
            <a
              href={BRAND.whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="text-white/60 hover:text-[color:var(--hd-gold)] transition-colors p-1"
              data-testid="header-whatsapp"
              aria-label="WhatsApp"
            >
              <WhatsAppIcon width={16} height={16} />
            </a>
            <a
              href={BRAND.tiktok}
              target="_blank"
              rel="noreferrer"
              className="text-white/60 hover:text-[color:var(--hd-gold)] transition-colors p-1"
              data-testid="header-tiktok"
              aria-label="TikTok"
            >
              <TikTokIcon width={16} height={16} />
            </a>
          </div>

          <div
            className="flex items-center gap-1 text-[11px] font-body tracking-wider"
            data-testid="language-switcher"
          >
            <button
              onClick={() => setLang("de")}
              className={`px-2 py-1 rounded transition-colors ${
                lang === "de"
                  ? "text-[color:var(--hd-gold)]"
                  : "text-white/50 hover:text-white"
              }`}
              data-testid="lang-de"
            >
              DE
            </button>
            <span className="text-white/20">/</span>
            <button
              onClick={() => setLang("ua")}
              className={`px-2 py-1 rounded transition-colors ${
                lang === "ua"
                  ? "text-[color:var(--hd-gold)]"
                  : "text-white/50 hover:text-white"
              }`}
              data-testid="lang-ua"
            >
              UA
            </button>
          </div>

          <button
            onClick={() => scrollTo("contact")}
            className="btn-gold !hidden md:!inline-flex"
            data-testid="header-cta"
          >
            {t.nav.cta}
          </button>

          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          className="lg:hidden glass mx-4 mt-2 rounded-2xl p-6 flex flex-col gap-4"
          data-testid="mobile-menu"
        >
          {NAV_IDS.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-left font-display text-lg text-white hover:text-[color:var(--hd-gold)]"
              data-testid={`mobile-nav-${item.id}`}
            >
              {t.nav[item.key]}
            </button>
          ))}
          <button
            onClick={() => scrollTo("contact")}
            className="btn-gold mt-2 justify-center"
            data-testid="mobile-cta"
          >
            {t.nav.cta}
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;
